import baseUrl from "../../../../api/baseUrl";
import socket from "../../../../api/socket";

export type OngoingConflictAppointment = {
  _id: string;
  status?: string;
  roomNumber?: string;
  sessionName?: string;
  queue?: number | string;
  patientId?: { fullName?: string };
  bookPatientId?: { fullName?: string };
};

export type OngoingResolution = "completed" | "waiting";

function localYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Map an appointment date to the list API day / filterDate headers. */
export function dayParamsForAppointment(item: {
  date?: string;
}): { day: string; filterDate?: string } {
  const dateStr = String(item?.date ?? "").split("T")[0]?.trim();
  if (!dateStr) return { day: "today" };

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (dateStr === localYmd(today)) return { day: "today" };
  if (dateStr === localYmd(tomorrow)) return { day: "tomorrow" };
  return { day: "all", filterDate: dateStr };
}

export function patientNameFromOngoingItem(
  item: OngoingConflictAppointment
): string {
  return (
    item?.patientId?.fullName ||
    item?.bookPatientId?.fullName ||
    "Unknown patient"
  );
}

export function normalizeRoom(room?: unknown): string {
  return String(room ?? "").trim();
}

/** Fetch other appointments still ongoing in the same room (same day). */
export async function fetchOtherOngoingInSameRoom(item: {
  _id?: string;
  date?: string;
  roomNumber?: unknown;
}): Promise<OngoingConflictAppointment[]> {
  const room = normalizeRoom(item?.roomNumber);
  if (!room || !item?._id) return [];

  const { day, filterDate } = dayParamsForAppointment(item);
  const results: OngoingConflictAppointment[] = [];
  let page = 1;

  while (page <= 10) {
    const response = await baseUrl.get("/appointment/findAllAppointments", {
      headers: {
        status: "ongoing",
        day,
        doctorid: "",
        page,
      },
      params: filterDate ? { filterDate } : undefined,
    });
    const rows: OngoingConflictAppointment[] = Array.isArray(
      response.data?.data
    )
      ? response.data.data
      : [];
    results.push(...rows);
    if (rows.length < 20) break;
    page += 1;
  }

  return results.filter(
    (row) =>
      String(row?._id) !== String(item._id) &&
      normalizeRoom(row?.roomNumber) === room
  );
}

export function emitStatusSocket(
  appointment: {
    _id?: string;
    queue?: unknown;
    roomNumber?: unknown;
    sessionName?: unknown;
  },
  status: string
) {
  if (!appointment?._id) return;
  socket.emit("allAppointmentStatus", {
    appointmentid: appointment._id,
    status,
    queue: appointment?.queue,
    roomNumber: appointment?.roomNumber,
    sessionName: appointment?.sessionName,
  });
}

export async function patchAppointmentStatus(
  appointment: {
    _id?: string;
    queue?: unknown;
    roomNumber?: unknown;
    sessionName?: unknown;
  },
  status: string
): Promise<void> {
  if (!appointment?._id) return;
  emitStatusSocket(appointment, status);
  await baseUrl.patch(
    "/appointment/statusAppointmentById",
    {},
    { headers: { appointmentid: appointment._id, status } }
  );
}

/** Apply completed / on-hold for conflicting room appointments. */
export async function resolveOngoingConflicts(
  resolutions: Array<{
    appointment: OngoingConflictAppointment;
    status: OngoingResolution;
  }>
): Promise<void> {
  await Promise.all(
    resolutions.map(({ appointment, status }) =>
      patchAppointmentStatus(appointment, status)
    )
  );
}
